import type { SettingDefinition, SettingValueType, SettingValue } from './types.js'

export function validateSettingValue<T extends SettingValueType>(
	definition: SettingDefinition<T>,
	value: unknown
): string | null {
	if (value === undefined || value === null) {
		return `Value for "${definition.key}" cannot be ${value}`
	}

	switch (definition.type) {
		case 'string':
			if (typeof value !== 'string')
				return `Expected string for "${definition.key}", got ${typeof value}`
			break

		case 'number':
			if (typeof value !== 'number' || !Number.isFinite(value)) {
				return `Expected finite number for "${definition.key}", got ${value}`
			}
			if (definition.minimum !== undefined && value < definition.minimum) {
				return `Value for "${definition.key}" must be >= ${definition.minimum}`
			}
			if (definition.maximum !== undefined && value > definition.maximum) {
				return `Value for "${definition.key}" must be <= ${definition.maximum}`
			}
			break

		case 'boolean':
			if (typeof value !== 'boolean')
				return `Expected boolean for "${definition.key}", got ${typeof value}`
			break

		case 'enum':
			if (typeof value !== 'string')
				return `Expected string for enum "${definition.key}", got ${typeof value}`
			if (definition.enumValues && !definition.enumValues.includes(value)) {
				return `Invalid enum value "${value}" for "${definition.key}". Allowed: ${definition.enumValues.join(', ')}`
			}
			break

		case 'object':
			if (typeof value !== 'object' || Array.isArray(value)) {
				return `Expected object for "${definition.key}", got ${typeof value}`
			}
			break
	}

	if (definition.validate) {
		return definition.validate(value as SettingValue<T>)
	}

	return null
}

export function clampNumber(value: number, definition: SettingDefinition<'number'>): number {
	let clamped = value
	if (definition.minimum !== undefined) clamped = Math.max(definition.minimum, clamped)
	if (definition.maximum !== undefined) clamped = Math.min(definition.maximum, clamped)
	if (definition.step !== undefined)
		clamped = Math.round(clamped / definition.step) * definition.step
	return clamped
}
